// SignupStep6.jsx — Profile Photo (final step, submits registration)
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import config from "../../config"; // adjust relative path to your project's config.js if this file lives deeper

// Where to send the user after "Continue" (their account still needs email
// verification, so they log in from here once verified).
const LOGIN_PATH = "/login";

// Signup is shown in a Bootstrap modal. Navigating away without closing it
// leaves the dark backdrop on screen and the page unscrollable, so close it
// properly first.
function closeOpenModals() {
  document.querySelectorAll(".modal.show").forEach((el) => {
    const Modal = window.bootstrap && window.bootstrap.Modal;
    // FIXED: Modal.getInstance is a Bootstrap 5-only static method. On
    // Bootstrap 4 (or if window.bootstrap isn't the real Bootstrap Modal
    // class), Modal exists but getInstance doesn't — calling it threw
    // "Modal.getInstance is not a function" instead of falling back to
    // the manual DOM cleanup below, which left the modal backdrop stuck
    // on screen after a successful signup.
    const instance =
      Modal && typeof Modal.getInstance === "function"
        ? Modal.getInstance(el)
        : null;

    if (instance) {
      instance.hide();
    } else {
      el.classList.remove("show");
      el.style.display = "none";
      el.setAttribute("aria-hidden", "true");
    }
  });
  document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
  document.body.classList.remove("modal-open");
  document.body.style.removeProperty("overflow");
  document.body.style.removeProperty("padding-right");
}

const Step6 = ({ formData, setFormData, prevStep, onSuccess }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Shown in place of the form once registration succeeds — the account is
  // 'pending' until the user verifies their email, so there's no auto-login.
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 15 * 1024 * 1024) {
      setError("File size should be less than 15MB");
      return;
    }
    const validTypes = ["image/jpeg", "image/png", "image/gif"];
    if (!validTypes.includes(file.type)) {
      setError("Only JPG, PNG, or GIF files are allowed");
      return;
    }

    setError(null);
    setFormData({ ...formData, profileImage: file });

    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemove = () => {
    setFormData({ ...formData, profileImage: null });
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Single multipart request — register() reads the photo off req.file and
  // the Basics & Lifestyle fields from Step 5 in the same request.
  const handleCreateAccount = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const body = new FormData();

      // Required fields
      body.append("firstName", formData.firstName || "");
      body.append("lastName", formData.lastName || "");
      body.append("email", formData.email || "");
      body.append("phone", formData.phone || "");
      if (formData.phoneCountryCode) body.append("phoneCountryCode", formData.phoneCountryCode);
      body.append("password", formData.password || "");
      body.append("country", formData.country || "");
      body.append("acceptedTerms", formData.acceptedTerms ? "true" : "false");

      // Basics & Lifestyle (Step 5) — optional
      if (formData.dob) body.append("dob", formData.dob);
      if (formData.maritalStatus) body.append("maritalStatus", formData.maritalStatus);
      if (formData.height) body.append("height", formData.height);
      if (formData.weight) body.append("weight", formData.weight);
      if (formData.grewUpIn) body.append("grewUpIn", formData.grewUpIn);
      if (formData.diet) body.append("diet", formData.diet);
      if (formData.bloodGroup) body.append("bloodGroup", formData.bloodGroup);
      if (formData.disability) body.append("disability", formData.disability);

      // Profile photo (this step) — optional
      if (formData.profileImage) {
        body.append("profileImage", formData.profileImage);
      }

      const res = await fetch(`${config.baseURL}/api/auth/register`, {
        method: "POST",
        body, // no Content-Type header — the browser sets the multipart boundary
      });
      const resBody = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(resBody.message || "Registration failed");
      }

      setSuccessMessage(
        resBody.message ||
          "Account created. Check your email to verify your account before logging in."
      );
      setSuccess(true);
    } catch (err) {
      setError(err.message || "Could not create your account");
    } finally {
      setSubmitting(false);
    }
  };

  // Always does something:
  //  1. let the parent handle it if it passed onSuccess
  //  2. otherwise (or if that throws) close the signup modal and go to login
  const handleContinue = () => {
    const payload = { message: successMessage, email: formData.email };

    if (typeof onSuccess === "function") {
      try {
        onSuccess(payload);
        return;
      } catch (err) {
        console.error("Signup onSuccess handler failed, going to login instead:", err);
      }
    }

    closeOpenModals();
    navigate(LOGIN_PATH, { replace: true, state: payload });
  };

  return (
    <div className="s6-step6">
      {/* Scoped styles, namespaced with "s6-" so other pages' classes can't leak in */}
      <style>{`
        .s6-step6 { position: relative; }
        .s6-back-btn {
          border: none;
          background: none;
          font-size: 18px;
          padding: 6px;
          cursor: pointer;
          line-height: 1;
        }
        .s6-icon-blk {
          text-align: center;
          margin: 4px 0 12px;
        }
        .s6-icon-blk img {
          width: 56px;
          height: 56px;
        }
        .s6-title {
          text-align: center;
          margin: 0 0 4px;
          font-weight: 600;
        }
        .s6-subtitle {
          text-align: center;
          font-size: 12px;
          color: #888;
          margin: 0 0 16px;
        }
        .s6-upload-box {
          width: 100%;
          min-height: 220px;
          border: 1px dashed #ccc;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          margin-bottom: 16px;
          background: #fafafa;
        }
        .s6-upload-label {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          cursor: pointer;
          padding: 24px;
          box-sizing: border-box;
        }
        .s6-preview-img {
          display: block;
          width: 120px;
          height: 120px;
          min-width: 120px;
          min-height: 120px;
          max-width: 120px;
          max-height: 120px;
          border-radius: 50%;
          object-fit: cover;
          flex-shrink: 0;
        }
        .s6-click-here {
          color: #2f6fed;
          font-weight: 600;
          font-size: 14px;
        }
        .s6-upload-hint {
          font-size: 13px;
          color: #555;
          margin-top: 2px;
        }
        .s6-camera-icon {
          width: 40px;
          margin-top: 8px;
        }
        .s6-remove-btn {
          display: block;
          margin: 0 auto 16px;
          border: none;
          background: none;
          color: #B3261E;
          font-size: 12px;
          cursor: pointer;
        }
        .s6-error {
          color: #B3261E;
          font-size: 12.5px;
          text-align: center;
          margin-bottom: 12px;
        }
        .s6-footer {
          display: flex;
          justify-content: center;
          margin-top: 16px;
          position: relative;
          z-index: 2; /* keep the button above any decorative overlay in the modal */
        }
        .s6-submit-btn {
          padding: 10px 32px;
          cursor: pointer;
        }
        .s6-success {
          text-align: center;
          padding: 12px 0 4px;
        }
        .s6-success-icon {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: #E8F5E9;
          color: #1E8E3E;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
          font-size: 28px;
          line-height: 1;
        }
        .s6-success-title {
          font-weight: 700;
          font-size: 18px;
          margin: 0 0 8px;
        }
        .s6-success-text {
          font-size: 13.5px;
          color: #555;
          margin: 0 0 24px;
          line-height: 1.5;
        }
      `}</style>

      {success ? (
        <div className="s6-success">
          <div className="s6-success-icon" aria-hidden="true">✓</div>
          <h5 className="s6-success-title">Account created!</h5>
          <p className="s6-success-text">{successMessage}</p>
          <div className="s6-footer">
            <button type="button" className="btn-filled s6-submit-btn" onClick={handleContinue}>
              Continue to login
            </button>
          </div>
        </div>
      ) : (
        <>
          <button type="button" className="s6-back-btn" onClick={prevStep} aria-label="Back" disabled={submitting}>
            <i className="fa fa-long-arrow-left" aria-hidden="true"></i>
          </button>

          <div className="s6-icon-blk">
            <img src="images/profile-icon.svg" alt="Profile photo" />
          </div>

          <div>
            <h5 className="s6-title">Add a profile photo</h5>
            <p className="s6-subtitle">
              Optional — you can add or change this later from your profile.
            </p>

            <div className="s6-upload-box">
              <label htmlFor="signupProfilePhoto" className="s6-upload-label">
                {preview ? (
                  <img src={preview} alt="Preview" className="s6-preview-img" />
                ) : (
                  <>
                    <span className="s6-click-here">Click here</span>
                    <span className="s6-upload-hint">to upload a photo</span>
                    <img src="images/camera.png" alt="" className="s6-camera-icon" />
                  </>
                )}
              </label>
              <input
                ref={fileInputRef}
                type="file"
                id="signupProfilePhoto"
                className="d-none"
                onChange={handleFileChange}
                accept="image/*"
                disabled={submitting}
              />
            </div>

            {preview && (
              <button type="button" className="s6-remove-btn" onClick={handleRemove} disabled={submitting}>
                Remove photo
              </button>
            )}

            {error && <p className="s6-error">{error}</p>}

            <div className="s6-footer">
              <button
                type="button"
                className="btn-filled s6-submit-btn"
                onClick={handleCreateAccount}
                disabled={submitting}
              >
                {submitting ? "Creating..." : "Create account"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Step6;